import IconCircleCheck from "central-icons/IconCircleCheck";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import IconChevronRight from "central-icons/IconChevronRight";
import {
    createContext,
    useContext,
    useRef,
    useState,
    useEffect,
    type FC,
    type ReactNode,
    Children,
    isValidElement,
    cloneElement,
} from "react";
import { useDisclosure, useButton, mergeProps, useFocusRing } from "react-aria";
import { useDisclosureState } from "react-stately";

type DisclosureStepContextValue = {
    index: number;
    currentStep: number;
    onStepChange: (step: number) => void;
    isCompleted: boolean;
    isActive: boolean;
    isLocked: boolean;
    canGoBack: boolean;
};

const DisclosureStepContext = createContext<DisclosureStepContextValue | null>(null);

const useDisclosureStepContext = () => {
    const context = useContext(DisclosureStepContext);
    if (!context) {
        throw new Error("DisclosureStepList.Item must be used within a DisclosureStepList");
    }
    return context;
};

export type DisclosureStepListProps = {
    /** The current active step index (0-based) */
    currentStep: number;
    /** Callback when step changes */
    onStepChange: (step: number) => void;
    /** DisclosureStepList.Item children */
    children: ReactNode;
    /** Additional CSS classes */
    className?: string;
    /** Whether users can navigate back to completed steps. Defaults to true. */
    canGoBack?: boolean;
};

const DisclosureStepListRoot: FC<DisclosureStepListProps> = ({
    currentStep,
    onStepChange,
    children,
    className,
    canGoBack = true,
}) => {
    const steps = Children.toArray(children).filter(isValidElement);

    // Find the highest completed step index
    const highestCompletedIndex = steps.reduce((highest, child, index) => {
        const childProps = (child as React.ReactElement).props as DisclosureStepItemProps;
        return childProps.completed === true ? index : highest;
    }, -1);

    return (
        <div className={cn("flex flex-col", className)}>
            {/* Using index as key is acceptable here since steps are static and declaratively defined */}
            {steps.map((child, index) => {
                const childProps = child.props as DisclosureStepItemProps;
                const isCompleted = childProps.completed === true;
                const isActive = index === currentStep;
                // A step is locked if it's not completed, not current, and not the next step after the highest completed
                const isNextStep = index === highestCompletedIndex + 1;
                const isLocked = !isCompleted && !isActive && !isNextStep;
                const isExpanded = index === currentStep;

                return (
                    <DisclosureStepContext.Provider
                        key={index}
                        value={{
                            index,
                            currentStep,
                            onStepChange,
                            isCompleted,
                            isActive,
                            isLocked,
                            canGoBack,
                        }}
                    >
                        {cloneElement(child, { isExpanded } as Partial<DisclosureStepItemProps>)}
                    </DisclosureStepContext.Provider>
                );
            })}
        </div>
    );
};

// ============================================================================
// DisclosureStepList.Item
// ============================================================================

export type DisclosureStepItemProps = {
    /** Step title */
    title: ReactNode;
    /** Optional step description shown below title when expanded */
    description?: ReactNode;
    /** Whether this step is completed */
    completed?: boolean;
    /** Step content */
    children: ReactNode;
    /** Additional CSS classes */
    className?: string;
    /** Controlled expanded state (used internally by DisclosureStepList) */
    isExpanded?: boolean;
};

const COLLAPSE_ANIMATION_DURATION = 200;

const DisclosureStepItem: FC<DisclosureStepItemProps> = ({
    title,
    description,
    children,
    className,
    isExpanded = false,
}) => {
    const { index, onStepChange, isCompleted, isLocked, canGoBack } = useDisclosureStepContext();

    // Delay unmounting children until collapse animation completes
    const [shouldRenderContent, setShouldRenderContent] = useState(isExpanded);

    useEffect(() => {
        if (isExpanded) {
            setShouldRenderContent(true);
        } else {
            const timer = setTimeout(() => {
                setShouldRenderContent(false);
            }, COLLAPSE_ANIMATION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [isExpanded]);

    // Completed steps cannot be clicked when canGoBack is false
    const isCompletedAndNoGoBack = isCompleted && !canGoBack;
    // Non-locked steps can be clicked, unless it's a completed step and canGoBack is false
    const isExpandable = !isLocked && !isCompletedAndNoGoBack;

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    // Use a dummy ref for useDisclosure to prevent it from setting hidden attribute on our panel
    const dummyRef = useRef<HTMLDivElement | null>(null);

    const disclosureProps = {
        isExpanded,
        isDisabled: isLocked,
        onExpandedChange: () => {
            if (isExpandable) {
                onStepChange(index);
            }
        },
    };

    const state = useDisclosureState(disclosureProps);
    const { buttonProps: triggerProps, panelProps } = useDisclosure(disclosureProps, state, dummyRef);
    const { buttonProps } = useButton(triggerProps, triggerRef);
    const { isFocusVisible, focusProps } = useFocusRing();

    const stepNumber = index + 1;

    return (
        <div className={cn("text-content-primary", className)}>
            <h3>
                <button
                    ref={triggerRef}
                    {...mergeProps(buttonProps, focusProps)}
                    className={cn("group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left", {
                        "focus:ring-1 focus:ring-border-brand focus:ring-offset-1": isFocusVisible,
                        "cursor-not-allowed opacity-50": isLocked,
                        "cursor-default": !isLocked && isCompletedAndNoGoBack,
                        "cursor-pointer hover:bg-surface-hover": !isLocked && !isCompletedAndNoGoBack,
                    })}
                    data-tracking-id={`disclosure-step-${stepNumber}`}
                >
                    <StepBadge stepNumber={stepNumber} isLocked={isLocked} />
                    <Text className="flex-1 text-content-primary">{title}</Text>
                    {isCompleted && <IconCircleCheck size={16} className="shrink-0 text-content-positive" />}
                    <IconChevronRight
                        size={10}
                        className={cn(
                            "shrink-0 text-content-secondary transition-transform",
                            isExpanded && "rotate-90",
                        )}
                    />
                </button>
            </h3>
            <div
                {...panelProps}
                className={cn(
                    "ml-[21px] grid border-l border-border-base pl-[22px] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
                    isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
            >
                <div
                    className={cn(
                        "min-h-0 transition-all duration-100 ease-out motion-reduce:transition-none",
                        isExpanded ? "scale-100 opacity-100 delay-75" : "opacity-0 delay-0",
                    )}
                >
                    {shouldRenderContent && (
                        <>
                            {description && (
                                <p className="pb-4 text-sm leading-none text-content-secondary">{description}</p>
                            )}
                            {children}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

type StepBadgeProps = {
    stepNumber: number;
    isLocked: boolean;
};

const StepBadge: FC<StepBadgeProps> = ({ stepNumber, isLocked }) => {
    return (
        <span
            className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-full bg-content-primary text-sm font-medium text-content-primary-inverted",
                isLocked && "opacity-50",
            )}
        >
            {stepNumber}
        </span>
    );
};

const DisclosureStepList = DisclosureStepListRoot as typeof DisclosureStepListRoot & {
    Item: typeof DisclosureStepItem;
};

DisclosureStepList.displayName = "DisclosureStepList";
DisclosureStepItem.displayName = "DisclosureStepList.Item";

DisclosureStepList.Item = DisclosureStepItem;

export { DisclosureStepList };
